// Translation catalogs. Keys are flat, dot-namespaced.
// EN and PT are complete; ES and IT cover navigation, chrome and key marketing
// strings. Missing keys fall back to English (see i18n/index.js).

export const LOCALES = {
  /* ===================================================================== */
  /* ENGLISH (complete, canonical)                                          */
  /* ===================================================================== */
  en: {
    // ---- Top bar / chrome ----
    "topbar.tagline": "A real, live person — verified in one redirect.",
    "topbar.status": "Liveness + anti-spoofing · Anti-deepfake · Injection-aware",

    // ---- Navigation ----
    "nav.home": "Home",
    "nav.onboarding": "Onboarding",
    "nav.auth": "Step-up Auth",
    "nav.features": "How it works",
    "nav.security": "Security",
    "nav.integrations": "Integrations",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "nav.bookDemo": "Book a demo",
    "nav.menu": "Menu",

    // ---- Common ----
    "common.learnMore": "Learn more",
    "common.bookDemo": "Book a demo",
    "common.contactSales": "Contact sales",
    "common.talkToUs": "Talk to us",
    "common.backHome": "Back to home",
    "common.language": "Language",
    "common.darkMode": "Dark mode",
    "common.lightMode": "Light mode",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",

    // ---- Home / hero ----
    "home.hero.eyebrow": "Facial liveness verification API",
    "home.hero.title1": "Prove it's a real,",
    "home.hero.titleHighlight": "live person.",
    "home.hero.lead":
      "SecFace is a drop-in facial liveness and anti-spoofing service. Redirect your user to a hosted check — face, motion, flash and gesture — and get a verifiable pass/fail back over an authenticated channel, backed by layered deep anti-spoofing (PAD).",
    "home.hero.ctaDemo": "Book a demo",
    "home.hero.panel": "Liveness check",
    "home.hero.status": "PAD passed · liveness confirmed",

    // ---- Home / use cases ----
    "home.verticals.eyebrow": "One liveness core, two jobs",
    "home.verticals.title": "The same verification, wherever identity matters",
    "home.verticals.subtitle":
      "Onboarding and step-up authentication share the same hosted liveness engine — passive + active anti-spoofing, face match and gesture — with flows tuned to each moment.",
    "home.verticals.onb.tag": "Onboarding",
    "home.verticals.onb.title": "Onboarding",
    "home.verticals.onb.desc":
      "Confirm a new user is a real, live human at signup. Document-to-selfie face match, passive liveness and an active gesture challenge — before the account exists.",
    "home.verticals.onb.cta": "Explore onboarding",
    "home.verticals.auth.tag": "Step-up authentication",
    "home.verticals.auth.title": "Auth & anti-fraud",
    "home.verticals.auth.desc":
      "Re-verify identity at login, password reset or high-value actions. Stop account takeover, deepfakes and camera-injection attacks with a fresh liveness check.",
    "home.verticals.auth.cta": "Explore step-up",

    // ---- Home / features ----
    "home.features.eyebrow": "Why SecFace",
    "home.features.title": "Everything you need to tell live from fake",
    "home.features.subtitle": "A hosted flow, layered anti-spoofing and an API built for a clean redirect.",
    "feat.redirect.title": "OAuth-style redirect",
    "feat.redirect.desc": "Start a session, redirect the browser, collect the result — like an OAuth flow, no SDK required.",
    "feat.pad.title": "Anti-spoofing (PAD)",
    "feat.pad.desc": "Multi-frame presentation-attack detection catches printouts, screens, masks and replays.",
    "feat.gesture.title": "Active gesture challenge",
    "feat.gesture.desc": "A randomized head, blink or smile prompt proves a live human is present, right now.",
    "feat.flash.title": "Flashmark passive liveness",
    "feat.flash.desc": "Controlled screen-flash reflection confirms a genuine 3D face — with no user effort.",
    "feat.facematch.title": "Document ↔ selfie match",
    "feat.facematch.desc": "ArcFace embeddings match the live face against a reference photo or ID document.",
    "feat.deep.title": "Layered deep anti-spoofing",
    "feat.deep.desc": "Multi-frame PAD, depth-from-motion and Flashmark catch the hardest spoofs — masks, deepfakes and replays.",
    "feat.multilang.title": "Multi-language hosted page",
    "feat.multilang.desc": "The verification page speaks EN, PT, ES and IT — set per session.",
    "feat.api.title": "One-time code, server-to-server",
    "feat.api.desc": "The browser only carries a single-use code; your backend fetches the verdict over Basic auth.",

    // ---- Home / how it works ----
    "home.how.eyebrow": "How it works",
    "home.how.title": "From start to verdict in four calls",
    "home.how.s1.title": "Start a session",
    "home.how.s1.desc": "Your backend POSTs /liveness/start and gets back a session_token and a verify_url.",
    "home.how.s2.title": "Redirect the user",
    "home.how.s2.desc": "Send the browser to the hosted page: face, motion, flash and an active gesture.",
    "home.how.s3.title": "Collect the verdict",
    "home.how.s3.desc": "SecFace redirects back with a one-time code; exchange it for the full result over Basic auth.",
    "home.how.s4.title": "One clear verdict",
    "home.how.s4.desc": "You get a single pass/fail verdict, backed by layered deep anti-spoofing — and it can never be flipped.",

    // ---- Home / compliance ----
    "home.compliance.eyebrow": "Security & compliance",
    "home.compliance.title": "Biometrics handled with care",
    "home.compliance.subtitle":
      "Encryption in transit and at rest, clear consent on the hosted page, data minimization and a signed DPA — the biometric result never rides in the browser redirect.",
    "home.compliance.privacy": "Privacy by design",
    "home.compliance.minimization": "Data minimization",
    "home.compliance.encryption": "TLS in transit · AES at rest",
    "home.compliance.dpa": "Immutable verdicts · DPA",

    "home.cta.title": "Add liveness to your flow in an afternoon",
    "home.cta.subtitle": "Spin up a sandbox tenant in minutes, or talk to us about scale and on-prem.",

    // ---- Onboarding page ----
    "onb.hero.eyebrow": "Onboarding",
    "onb.hero.title": "Onboard real people, not fakes",
    "onb.hero.lead":
      "For fintechs, marketplaces, gig and gaming platforms: a guided, hosted check that a new user is a real, live person — document-to-selfie match, passive liveness and an active gesture — before the account is created.",
    "onb.f.docmatch.title": "Document ↔ selfie match",
    "onb.f.docmatch.desc": "Compare the live face to an ID photo or an existing reference with ArcFace embeddings.",
    "onb.f.passive.title": "Passive liveness",
    "onb.f.passive.desc": "Frictionless anti-spoofing that runs while the user simply looks at the camera.",
    "onb.f.gesture.title": "Active gesture",
    "onb.f.gesture.desc": "A randomized turn, blink or smile challenge for a stronger human-present signal.",
    "onb.f.flash.title": "Flashmark",
    "onb.f.flash.desc": "Screen-flash reflection analysis confirms a real 3D face, not a screen or print.",
    "onb.f.deep.title": "Deep PAD pass",
    "onb.f.deep.desc": "Multi-frame PAD and depth-from-motion catch the hardest spoofing attempts.",
    "onb.f.hosted.title": "Hosted & ready",
    "onb.f.hosted.desc": "A ready-made verification page you redirect to — nothing to build, EN/PT/ES/IT.",
    "onb.f.gate.title": "Server-side gate",
    "onb.f.gate.desc": "The pass/fail decision is made server-side and can never be forged from the redirect.",
    "onb.f.policy.title": "Per-session policy",
    "onb.f.policy.desc": "Tune the AI threshold, require geolocation or require the gesture per session.",
    "onb.showcase.title": "A capture your fraud team can trust",
    "onb.showcase.desc":
      "Every session returns the face crop, full frame and gesture still, plus liveness and PAD scores — with an immutable verdict.",
    "onb.compare.title": "With vs. without SecFace",
    "onb.compare.without": "Without",
    "onb.compare.with": "With SecFace",
    "onb.faq.title": "Onboarding FAQ",

    // ---- Step-up auth page ----
    "stepup.hero.eyebrow": "Step-up authentication",
    "stepup.hero.title": "Stop account takeover at the moment it matters",
    "stepup.hero.lead":
      "Re-verify a returning user with a fresh liveness check at login, password reset, payout or any high-risk action — blocking stolen credentials, deepfakes and camera-injection attacks.",
    "stepup.f.stepup.title": "Step-up on demand",
    "stepup.f.stepup.desc": "Trigger a liveness check exactly when risk spikes — login, reset, high-value transfer.",
    "stepup.f.takeover.title": "Account-takeover defense",
    "stepup.f.takeover.desc": "A password is not enough; a live face is. Bind the session to a real person.",
    "stepup.f.deepfake.title": "Deepfake resistant",
    "stepup.f.deepfake.desc": "Active gesture + Flashmark + multi-frame PAD defeat rendered and replayed faces.",
    "stepup.f.injection.title": "Injection-attack aware",
    "stepup.f.injection.desc": "Flags virtual cameras and stream injection that try to bypass the real device camera.",
    "stepup.f.gate.title": "Immutable verdict",
    "stepup.f.gate.desc": "A denied check can never be flipped to approved on the same session — retries start fresh.",
    "stepup.f.deep.title": "Deep evidence",
    "stepup.f.deep.desc": "Multi-frame PAD, depth and Flashmark enrich the record for dispute review.",
    "stepup.f.webhook.title": "One-time code",
    "stepup.f.webhook.desc": "The browser only carries a single-use code; your backend confirms the verdict before granting access.",
    "stepup.showcase.title": "Fits your existing auth",
    "stepup.showcase.desc":
      "Drop the redirect between your login and your session issuance. SecFace returns a one-time code; your backend exchanges it before granting access.",
    "stepup.audience.apps": "For product & platform teams",
    "stepup.audience.security": "For fraud & security teams",

    // ---- Features / how-it-works page ----
    "features.hero.eyebrow": "Liveness engine",
    "features.hero.title": "Engineered to tell live from fake",
    "features.hero.lead": "The capabilities behind every SecFace verification — approachable, with a “for engineers” deep dive.",
    "features.forEngineers": "For engineers",
    "features.arch.title": "The integration flow",
    "features.arch.start": "Client backend · /liveness/start",
    "features.arch.hosted": "Hosted page · face + flash + gesture",
    "features.arch.gate": "Liveness + deep PAD · pass/fail",
    "features.arch.code": "Redirect back · one-time code",
    "features.arch.fetch": "Client fetch · /liveness/result",
    "features.arch.deep": "Deep anti-spoofing (PAD)",
    "features.arch.webhook": "Result to your backend",

    // ---- Security page ----
    "security.hero.eyebrow": "Security & Compliance",
    "security.hero.title": "Biometrics you can defend, verdicts you can trust",
    "security.hero.lead": "Security architecture, one-time codes, immutable verdicts, per-tenant isolation and a Data Processing Agreement.",

    // ---- Contact / demo ----
    "contact.hero.eyebrow": "Contact & demo",
    "contact.hero.title": "See SecFace in your own signup or login flow",
    "contact.hero.lead": "Tell us about your use case and we’ll tailor a sandbox. Reach us by email or WhatsApp — we reply within one business day.",
    "contact.channels.title": "Choose how to reach us",
    "contact.channels.subtitle": "No forms. Email us or start a WhatsApp chat — we reply within one business day.",
    "contact.email.title": "Email",
    "contact.email.cta": "Send email",
    "contact.whatsapp.title": "WhatsApp",
    "contact.whatsapp.desc": "Chat with our team now.",
    "contact.whatsapp.cta": "Open WhatsApp",

    // ---- Live demo flow ----
    "demo.cta": "Try a live demo",
    "demo.modal.title": "Try a live liveness demo",
    "demo.modal.subtitle": "Enter your name and e-mail — we’ll send you a 6-character code to start.",
    "demo.form.name": "Full name",
    "demo.form.namePh": "Your name",
    "demo.form.email": "E-mail",
    "demo.form.emailPh": "you@company.com",
    "demo.form.whatsapp": "WhatsApp (optional)",
    "demo.form.whatsappPh": "+55 41 99999-8888",
    "demo.form.whatsappHint": "Fill it in to also receive the code on WhatsApp.",
    "demo.form.consent": "I agree to the processing of my data and a face-liveness capture for this demo, per the Privacy Policy.",
    "demo.form.submit": "Send my code",
    "demo.token.title": "Enter your code",
    "demo.token.sentTo": "We sent a 6-character code to {email}. Enter it below to start the liveness check.",
    "demo.token.sentToBoth": "We sent a 6-character code to {email} and to WhatsApp {phone}. Enter it below to start the liveness check.",
    "demo.token.label": "Verification code",
    "demo.token.submit": "Confirm & start",
    "demo.token.resend": "Resend code",
    "demo.token.back": "Back",
    "demo.starting": "Starting your liveness check…",
    "demo.err.start": "We couldn’t send your code. Please check your e-mail and try again.",
    "demo.err.token": "Invalid or expired code.",
    "demo.err.generic": "Something went wrong. Please try again.",
    "demo.result.title.passed": "Liveness verified",
    "demo.result.title.failed": "Liveness not verified",
    "demo.result.title.expired": "Session expired",
    "demo.result.title.unknown": "Verification finished",
    "demo.result.lead.passed": "You were confirmed as a real, live person.",
    "demo.result.lead.failed": "We couldn’t confirm liveness this time. You can try the demo again.",
    "demo.result.lead.expired": "The verification window expired before it finished. You can try again.",
    "demo.result.lead.unknown": "Thanks for trying the SecFace liveness demo.",
    "demo.result.email": "In a few minutes you’ll receive the complete liveness report by e-mail.",
    "demo.result.emailSent": "We sent a confirmation to {email} — the complete report arrives shortly.",
    "demo.result.back": "Back to home",
    "demo.result.again": "Run another demo",
    "demo.note": "This demo runs only the liveness validation step. To see the full dashboard and platform, get in touch.",
    "demo.result.platformTitle": "Want the full platform?",
    "demo.result.platformText": "This demo shows only the liveness validation. To explore the complete SecFace dashboard, integrations and administration, talk to our team.",
    "demo.result.platformCta": "Talk to our team",

    // ---- Footer ----
    "footer.tagline": "A real, live person — verified in one redirect.",
    "footer.product": "Product",
    "footer.usecases": "Use cases",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.dpa": "DPA",
    "footer.about": "About",
    "footer.rights": "All rights reserved.",
    "footer.madeBy": "A sec4us company",

    // ---- Cookie banner ----
    "cookie.text": "We use cookies to run the site and improve your experience. Users verified by SecFace always see a clear consent and recording notice on the hosted page.",
    "cookie.accept": "Accept",
    "cookie.decline": "Decline",

    // ---- 404 ----
    "nf.title": "Page not found",
    "nf.body": "The page you’re looking for doesn’t exist or has moved.",
  },

  /* ===================================================================== */
  /* PORTUGUÊS (completo)                                                    */
  /* ===================================================================== */
  pt: {
    "topbar.tagline": "Uma pessoa real e viva — verificada em um redirect.",
    "topbar.status": "Liveness + anti-spoofing · Anti-deepfake · Ciente de injeção",

    "nav.home": "Início",
    "nav.onboarding": "Onboarding",
    "nav.auth": "Autenticação",
    "nav.features": "Como funciona",
    "nav.security": "Segurança",
    "nav.integrations": "Integrações",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contato",
    "nav.bookDemo": "Agendar demo",
    "nav.menu": "Menu",

    "common.learnMore": "Saiba mais",
    "common.bookDemo": "Agendar demo",
    "common.contactSales": "Falar com vendas",
    "common.talkToUs": "Fale conosco",
    "common.backHome": "Voltar ao início",
    "common.language": "Idioma",
    "common.darkMode": "Modo escuro",
    "common.lightMode": "Modo claro",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",

    "home.hero.eyebrow": "API de verificação de liveness facial",
    "home.hero.title1": "Prove que é uma pessoa",
    "home.hero.titleHighlight": "real e viva.",
    "home.hero.lead":
      "O SecFace é um serviço plug-and-play de liveness facial e anti-spoofing. Redirecione o usuário para uma verificação hospedada — rosto, movimento, flash e gesto — e receba um veredito verificável por um canal autenticado, com anti-spoofing profundo (PAD) em camadas.",
    "home.hero.ctaDemo": "Agendar demo",
    "home.hero.panel": "Verificação de liveness",
    "home.hero.status": "PAD aprovado · liveness confirmado",

    "home.verticals.eyebrow": "Um núcleo de liveness, dois papéis",
    "home.verticals.title": "A mesma verificação, onde quer que a identidade importe",
    "home.verticals.subtitle":
      "Onboarding e autenticação step-up compartilham o mesmo motor hospedado de liveness — anti-spoofing passivo + ativo, comparação facial e gesto — com fluxos ajustados a cada momento.",
    "home.verticals.onb.tag": "Onboarding",
    "home.verticals.onb.title": "Onboarding",
    "home.verticals.onb.desc":
      "Confirme que um novo usuário é uma pessoa real e viva no cadastro. Comparação documento-selfie, liveness passivo e desafio de gesto ativo — antes de a conta existir.",
    "home.verticals.onb.cta": "Explorar onboarding",
    "home.verticals.auth.tag": "Autenticação step-up",
    "home.verticals.auth.title": "Autenticação & antifraude",
    "home.verticals.auth.desc":
      "Reverifique a identidade no login, na troca de senha ou em ações de alto valor. Bloqueie account takeover, deepfakes e ataques de injeção de câmera com um novo liveness.",
    "home.verticals.auth.cta": "Explorar step-up",

    "home.features.eyebrow": "Por que SecFace",
    "home.features.title": "Tudo que você precisa para separar vivo de falso",
    "home.features.subtitle": "Um fluxo hospedado, anti-spoofing em camadas e uma API feita para um redirect limpo.",
    "feat.redirect.title": "Redirect estilo OAuth",
    "feat.redirect.desc": "Inicie uma sessão, redirecione o navegador, colete o resultado — como um fluxo OAuth, sem SDK.",
    "feat.pad.title": "Anti-spoofing (PAD)",
    "feat.pad.desc": "Detecção de ataque de apresentação multi-frame pega impressões, telas, máscaras e replays.",
    "feat.gesture.title": "Desafio de gesto ativo",
    "feat.gesture.desc": "Um comando aleatório de cabeça, piscada ou sorriso prova que há um humano vivo, agora.",
    "feat.flash.title": "Liveness passivo Flashmark",
    "feat.flash.desc": "A reflexão de um flash controlado da tela confirma um rosto 3D real — sem esforço do usuário.",
    "feat.facematch.title": "Comparação documento ↔ selfie",
    "feat.facematch.desc": "Embeddings ArcFace comparam o rosto vivo com uma foto de referência ou documento.",
    "feat.deep.title": "Anti-spoofing profundo em camadas",
    "feat.deep.desc": "PAD multi-frame, depth-from-motion e Flashmark pegam os spoofs mais difíceis — máscaras, deepfakes e replays.",
    "feat.multilang.title": "Página hospedada multi-idioma",
    "feat.multilang.desc": "A página de verificação fala EN, PT, ES e IT — definida por sessão.",
    "feat.api.title": "Código de uso único, servidor a servidor",
    "feat.api.desc": "O navegador só carrega um código de uso único; seu backend busca o veredito via Basic auth.",

    "home.how.eyebrow": "Como funciona",
    "home.how.title": "Do início ao veredito em quatro chamadas",
    "home.how.s1.title": "Inicie uma sessão",
    "home.how.s1.desc": "Seu backend faz POST /liveness/start e recebe um session_token e uma verify_url.",
    "home.how.s2.title": "Redirecione o usuário",
    "home.how.s2.desc": "Envie o navegador para a página hospedada: rosto, movimento, flash e um gesto ativo.",
    "home.how.s3.title": "Colete o veredito",
    "home.how.s3.desc": "O SecFace redireciona de volta com um código de uso único; troque-o pelo resultado via Basic auth.",
    "home.how.s4.title": "Um veredito claro",
    "home.how.s4.desc": "Você recebe um único pass/fail, respaldado por anti-spoofing profundo em camadas — e ele nunca pode ser revertido.",

    "home.compliance.eyebrow": "Segurança & compliance",
    "home.compliance.title": "Biometria tratada com cuidado",
    "home.compliance.subtitle":
      "Criptografia em trânsito e em repouso, consentimento claro na página hospedada, minimização de dados e um DPA assinado — o resultado biométrico nunca trafega no redirect do navegador.",
    "home.compliance.privacy": "Privacidade desde o design",
    "home.compliance.minimization": "Minimização de dados",
    "home.compliance.encryption": "TLS em trânsito · AES em repouso",
    "home.compliance.dpa": "Vereditos imutáveis · DPA",

    "home.cta.title": "Adicione liveness ao seu fluxo em uma tarde",
    "home.cta.subtitle": "Suba um tenant de sandbox em minutos ou fale com a gente sobre escala e on-premise.",

    "onb.hero.eyebrow": "Onboarding",
    "onb.hero.title": "Faça onboarding de pessoas reais, não de fraudes",
    "onb.hero.lead":
      "Para fintechs, marketplaces e plataformas de gig e gaming: uma verificação guiada e hospedada de que o novo usuário é uma pessoa real e viva — comparação documento-selfie, liveness passivo e gesto ativo — antes de a conta ser criada.",
    "onb.f.docmatch.title": "Comparação documento ↔ selfie",
    "onb.f.docmatch.desc": "Compare o rosto vivo com a foto de um documento ou referência existente via embeddings ArcFace.",
    "onb.f.passive.title": "Liveness passivo",
    "onb.f.passive.desc": "Anti-spoofing sem atrito que roda enquanto o usuário apenas olha para a câmera.",
    "onb.f.gesture.title": "Gesto ativo",
    "onb.f.gesture.desc": "Um desafio aleatório de virar, piscar ou sorrir para um sinal mais forte de humano presente.",
    "onb.f.flash.title": "Flashmark",
    "onb.f.flash.desc": "A análise da reflexão do flash da tela confirma um rosto 3D real, não uma tela ou impressão.",
    "onb.f.deep.title": "Passo profundo de PAD",
    "onb.f.deep.desc": "PAD multi-frame e depth-from-motion pegam as tentativas de spoofing mais difíceis.",
    "onb.f.hosted.title": "Hospedado e pronto",
    "onb.f.hosted.desc": "Uma página de verificação pronta para redirecionar — nada a construir, EN/PT/ES/IT.",
    "onb.f.gate.title": "Gate no servidor",
    "onb.f.gate.desc": "A decisão de aprovar/reprovar é feita no servidor e nunca pode ser forjada pelo redirect.",
    "onb.f.policy.title": "Política por sessão",
    "onb.f.policy.desc": "Ajuste o threshold da IA, exija geolocalização ou exija o gesto por sessão.",
    "onb.showcase.title": "Uma captura em que seu time antifraude confia",
    "onb.showcase.desc":
      "Cada sessão retorna o recorte do rosto, o quadro completo e a foto do gesto, além dos scores de liveness e PAD — com veredito imutável.",
    "onb.compare.title": "Com vs. sem SecFace",
    "onb.compare.without": "Sem",
    "onb.compare.with": "Com SecFace",
    "onb.faq.title": "Perguntas frequentes — Onboarding",

    "stepup.hero.eyebrow": "Autenticação step-up",
    "stepup.hero.title": "Barre o account takeover no momento em que importa",
    "stepup.hero.lead":
      "Reverifique um usuário recorrente com um novo liveness no login, na troca de senha, num saque ou em qualquer ação de risco — bloqueando credenciais roubadas, deepfakes e ataques de injeção de câmera.",
    "stepup.f.stepup.title": "Step-up sob demanda",
    "stepup.f.stepup.desc": "Dispare um liveness exatamente quando o risco sobe — login, reset, transferência de alto valor.",
    "stepup.f.takeover.title": "Defesa contra takeover",
    "stepup.f.takeover.desc": "Uma senha não basta; um rosto vivo basta. Vincule a sessão a uma pessoa real.",
    "stepup.f.deepfake.title": "Resistente a deepfake",
    "stepup.f.deepfake.desc": "Gesto ativo + Flashmark + PAD multi-frame derrotam rostos renderizados e reproduzidos.",
    "stepup.f.injection.title": "Ciente de injeção",
    "stepup.f.injection.desc": "Sinaliza câmeras virtuais e injeção de stream que tentam burlar a câmera real do dispositivo.",
    "stepup.f.gate.title": "Veredito imutável",
    "stepup.f.gate.desc": "Uma verificação negada nunca vira aprovada na mesma sessão — novas tentativas recomeçam do zero.",
    "stepup.f.deep.title": "Evidência profunda",
    "stepup.f.deep.desc": "PAD multi-frame, profundidade e Flashmark enriquecem o registro para revisão de disputas.",
    "stepup.f.webhook.title": "Código de uso único",
    "stepup.f.webhook.desc": "O navegador só carrega um código de uso único; seu backend confirma o veredito antes de conceder acesso.",
    "stepup.showcase.title": "Encaixa na sua autenticação atual",
    "stepup.showcase.desc":
      "Coloque o redirect entre o seu login e a emissão da sessão. O SecFace retorna um código de uso único; seu backend o troca antes de conceder acesso.",
    "stepup.audience.apps": "Para times de produto & plataforma",
    "stepup.audience.security": "Para times de fraude & segurança",

    "features.hero.eyebrow": "Motor de liveness",
    "features.hero.title": "Projetado para separar vivo de falso",
    "features.hero.lead": "As capacidades por trás de cada verificação SecFace — acessíveis, com um aprofundamento “para técnicos”.",
    "features.forEngineers": "Para técnicos",
    "features.arch.title": "O fluxo de integração",
    "features.arch.start": "Backend do cliente · /liveness/start",
    "features.arch.hosted": "Página hospedada · rosto + flash + gesto",
    "features.arch.gate": "Liveness + PAD profundo · pass/fail",
    "features.arch.code": "Redirect de volta · código único",
    "features.arch.fetch": "Fetch do cliente · /liveness/result",
    "features.arch.deep": "Anti-spoofing profundo (PAD)",
    "features.arch.webhook": "Resultado no seu backend",

    "security.hero.eyebrow": "Segurança & Compliance",
    "security.hero.title": "Biometria que você defende, vereditos em que você confia",
    "security.hero.lead": "Arquitetura de segurança, códigos de uso único, vereditos imutáveis, isolamento por tenant e um Acordo de Processamento de Dados (DPA).",

    "contact.hero.eyebrow": "Contato & demo",
    "contact.hero.title": "Veja o SecFace no seu próprio fluxo de cadastro ou login",
    "contact.hero.lead": "Conte sobre seu caso de uso e preparamos um sandbox. Fale com a gente por e-mail ou WhatsApp — respondemos em até um dia útil.",
    "contact.channels.title": "Escolha como falar com a gente",
    "contact.channels.subtitle": "Sem formulários. Envie um e-mail ou inicie uma conversa no WhatsApp — respondemos em até um dia útil.",
    "contact.email.title": "E-mail",
    "contact.email.cta": "Enviar e-mail",
    "contact.whatsapp.title": "WhatsApp",
    "contact.whatsapp.desc": "Fale com nosso time agora.",
    "contact.whatsapp.cta": "Abrir WhatsApp",

    // ---- Fluxo de demo ao vivo ----
    "demo.cta": "Fazer uma Demo agora",
    "demo.modal.title": "Faça uma demo de liveness ao vivo",
    "demo.modal.subtitle": "Informe seu nome e e-mail — enviaremos um código de 6 caracteres para começar.",
    "demo.form.name": "Nome completo",
    "demo.form.namePh": "Seu nome",
    "demo.form.email": "E-mail",
    "demo.form.emailPh": "voce@empresa.com",
    "demo.form.whatsapp": "WhatsApp (opcional)",
    "demo.form.whatsappPh": "+55 41 99999-8888",
    "demo.form.whatsappHint": "Preencha para receber o código também pelo WhatsApp.",
    "demo.form.consent": "Concordo com o tratamento dos meus dados e com uma captura de liveness facial para esta demonstração, conforme a Política de Privacidade.",
    "demo.form.submit": "Enviar meu código",
    "demo.token.title": "Digite seu código",
    "demo.token.sentTo": "Enviamos um código de 6 caracteres para {email}. Digite-o abaixo para iniciar a verificação de liveness.",
    "demo.token.sentToBoth": "Enviamos um código de 6 caracteres para {email} e para o WhatsApp {phone}. Digite-o abaixo para iniciar a verificação de liveness.",
    "demo.token.label": "Código de verificação",
    "demo.token.submit": "Confirmar e iniciar",
    "demo.token.resend": "Reenviar código",
    "demo.token.back": "Voltar",
    "demo.starting": "Iniciando sua verificação de liveness…",
    "demo.err.start": "Não conseguimos enviar seu código. Verifique o e-mail e tente novamente.",
    "demo.err.token": "Código inválido ou expirado.",
    "demo.err.generic": "Algo deu errado. Tente novamente.",
    "demo.result.title.passed": "Liveness verificado",
    "demo.result.title.failed": "Liveness não verificado",
    "demo.result.title.expired": "Sessão expirada",
    "demo.result.title.unknown": "Verificação concluída",
    "demo.result.lead.passed": "Você foi confirmado como uma pessoa real e viva.",
    "demo.result.lead.failed": "Não foi possível confirmar o liveness desta vez. Você pode refazer a demo.",
    "demo.result.lead.expired": "A janela de verificação expirou antes de concluir. Você pode tentar de novo.",
    "demo.result.lead.unknown": "Obrigado por testar a demo de liveness do SecFace.",
    "demo.result.email": "Em alguns minutos você receberá o relatório completo do liveness por e-mail.",
    "demo.result.emailSent": "Enviamos uma confirmação para {email} — o relatório completo chega em alguns minutos.",
    "demo.result.back": "Voltar ao início",
    "demo.result.again": "Fazer nova demo",
    "demo.note": "Esta demo executa apenas a etapa de validação de liveness. Para conhecer o painel e a plataforma completa, entre em contato.",
    "demo.result.platformTitle": "Quer a plataforma completa?",
    "demo.result.platformText": "Esta demo mostra apenas a validação de liveness. Para conhecer o painel completo do SecFace, integrações e administração, fale com nosso time.",
    "demo.result.platformCta": "Falar com nosso time",

    "footer.tagline": "Uma pessoa real e viva — verificada em um redirect.",
    "footer.product": "Produto",
    "footer.usecases": "Casos de uso",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidade",
    "footer.terms": "Termos de Serviço",
    "footer.dpa": "DPA",
    "footer.about": "Sobre",
    "footer.rights": "Todos os direitos reservados.",
    "footer.madeBy": "Uma empresa sec4us",

    "cookie.text": "Usamos cookies para operar o site e melhorar sua experiência. Usuários verificados pelo SecFace sempre veem um aviso claro de consentimento e gravação na página hospedada.",
    "cookie.accept": "Aceitar",
    "cookie.decline": "Recusar",

    "nf.title": "Página não encontrada",
    "nf.body": "A página que você procura não existe ou foi movida.",
  },

  /* ===================================================================== */
  /* ESPAÑOL (navegación + marketing clave)                                 */
  /* ===================================================================== */
  es: {
    "topbar.tagline": "Una persona real y viva — verificada en un redirect.",
    "topbar.status": "Liveness + anti-spoofing · Anti-deepfake · Consciente de inyección",

    "nav.home": "Inicio",
    "nav.onboarding": "Onboarding",
    "nav.auth": "Autenticación",
    "nav.features": "Cómo funciona",
    "nav.security": "Seguridad",
    "nav.integrations": "Integraciones",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contacto",
    "nav.bookDemo": "Agendar demo",
    "nav.menu": "Menú",

    "common.learnMore": "Saber más",
    "common.bookDemo": "Agendar demo",
    "common.contactSales": "Hablar con ventas",
    "common.talkToUs": "Contáctanos",
    "common.backHome": "Volver al inicio",
    "common.language": "Idioma",
    "common.darkMode": "Modo oscuro",
    "common.lightMode": "Modo claro",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",

    "home.hero.eyebrow": "API de verificación de liveness facial",
    "home.hero.title1": "Prueba que es una persona",
    "home.hero.titleHighlight": "real y viva.",
    "home.hero.lead":
      "SecFace es un servicio plug-and-play de liveness facial y anti-spoofing. Redirige al usuario a una verificación alojada — rostro, movimiento, flash y gesto — y recibe un veredicto verificable por un canal autenticado, con anti-spoofing profundo (PAD) por capas.",
    "home.hero.ctaDemo": "Agendar demo",
    "home.hero.panel": "Verificación de liveness",
    "home.hero.status": "PAD aprobado · liveness confirmado",

    "home.verticals.eyebrow": "Un núcleo de liveness, dos funciones",
    "home.verticals.title": "La misma verificación, donde importe la identidad",
    "home.verticals.subtitle":
      "El onboarding y la autenticación step-up comparten el mismo motor alojado de liveness — anti-spoofing pasivo + activo, comparación facial y gesto — con flujos adaptados a cada momento.",
    "home.verticals.onb.tag": "Onboarding",
    "home.verticals.onb.title": "Onboarding",
    "home.verticals.onb.desc":
      "Confirma que un nuevo usuario es una persona real y viva en el registro. Comparación documento-selfie, liveness pasivo y desafío de gesto activo, antes de crear la cuenta.",
    "home.verticals.onb.cta": "Explorar onboarding",
    "home.verticals.auth.tag": "Autenticación step-up",
    "home.verticals.auth.title": "Autenticación & antifraude",
    "home.verticals.auth.desc":
      "Reverifica la identidad en el login, el cambio de contraseña o acciones de alto valor. Detén el account takeover, los deepfakes y los ataques de inyección de cámara con un nuevo liveness.",
    "home.verticals.auth.cta": "Explorar step-up",

    "home.features.eyebrow": "Por qué SecFace",
    "home.features.title": "Todo lo necesario para separar vivo de falso",
    "home.features.subtitle": "Un flujo alojado, anti-spoofing en capas y una API pensada para un redirect limpio.",

    "home.how.eyebrow": "Cómo funciona",
    "home.how.title": "Del inicio al veredicto en cuatro llamadas",

    "home.compliance.privacy": "Privacidad por diseño",
    "home.compliance.minimization": "Minimización de datos",
    "home.compliance.encryption": "TLS en tránsito · AES en reposo",
    "home.compliance.dpa": "Veredictos inmutables · DPA",

    "home.cta.title": "Añade liveness a tu flujo en una tarde",
    "home.cta.subtitle": "Lanza un tenant sandbox en minutos o habla con nosotros sobre escala y on-premise.",

    "footer.tagline": "Una persona real y viva — verificada en un redirect.",
    "footer.product": "Producto",
    "footer.usecases": "Casos de uso",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos de Servicio",
    "footer.rights": "Todos los derechos reservados.",
    "footer.madeBy": "Una empresa sec4us",

    "cookie.text": "Usamos cookies para operar el sitio y mejorar tu experiencia. Los usuarios verificados por SecFace siempre ven un aviso claro de consentimiento y grabación en la página alojada.",
    "cookie.accept": "Aceptar",
    "cookie.decline": "Rechazar",

    "nf.title": "Página no encontrada",
    "nf.body": "La página que buscas no existe o se ha movido.",
  },

  /* ===================================================================== */
  /* ITALIANO (navigazione + marketing chiave)                              */
  /* ===================================================================== */
  it: {
    "topbar.tagline": "Una persona reale e viva — verificata in un redirect.",
    "topbar.status": "Liveness + anti-spoofing · Anti-deepfake · Consapevole dell’iniezione",

    "nav.home": "Home",
    "nav.onboarding": "Onboarding",
    "nav.auth": "Autenticazione",
    "nav.features": "Come funziona",
    "nav.security": "Sicurezza",
    "nav.integrations": "Integrazioni",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contatti",
    "nav.bookDemo": "Prenota demo",
    "nav.menu": "Menu",

    "common.learnMore": "Scopri di più",
    "common.bookDemo": "Prenota demo",
    "common.contactSales": "Contatta le vendite",
    "common.talkToUs": "Contattaci",
    "common.backHome": "Torna alla home",
    "common.language": "Lingua",
    "common.darkMode": "Modalità scura",
    "common.lightMode": "Modalità chiara",
    "common.cancel": "Annulla",
    "common.confirm": "Conferma",

    "home.hero.eyebrow": "API di verifica del liveness facciale",
    "home.hero.title1": "Dimostra che è una persona",
    "home.hero.titleHighlight": "reale e viva.",
    "home.hero.lead":
      "SecFace è un servizio plug-and-play di liveness facciale e anti-spoofing. Reindirizza l’utente a una verifica ospitata — volto, movimento, flash e gesto — e ricevi un verdetto verificabile su un canale autenticato, con anti-spoofing profondo (PAD) a livelli.",
    "home.hero.ctaDemo": "Prenota demo",
    "home.hero.panel": "Verifica del liveness",
    "home.hero.status": "PAD superato · liveness confermato",

    "home.verticals.eyebrow": "Un core di liveness, due ruoli",
    "home.verticals.title": "La stessa verifica, ovunque conti l’identità",
    "home.verticals.subtitle":
      "Onboarding e autenticazione step-up condividono lo stesso motore ospitato di liveness — anti-spoofing passivo + attivo, confronto del volto e gesto — con flussi adattati a ogni momento.",
    "home.verticals.onb.tag": "Onboarding",
    "home.verticals.onb.title": "Onboarding",
    "home.verticals.onb.desc":
      "Conferma che un nuovo utente è una persona reale e viva alla registrazione. Confronto documento-selfie, liveness passivo e sfida di gesto attivo, prima che l’account esista.",
    "home.verticals.onb.cta": "Esplora onboarding",
    "home.verticals.auth.tag": "Autenticazione step-up",
    "home.verticals.auth.title": "Autenticazione & antifrode",
    "home.verticals.auth.desc":
      "Riverifica l’identità al login, al reset della password o in azioni di alto valore. Ferma account takeover, deepfake e attacchi di iniezione della camera con un nuovo liveness.",
    "home.verticals.auth.cta": "Esplora step-up",

    "home.features.eyebrow": "Perché SecFace",
    "home.features.title": "Tutto per distinguere il vivo dal falso",
    "home.features.subtitle": "Un flusso ospitato, anti-spoofing a livelli e un’API pensata per un redirect pulito.",

    "home.how.eyebrow": "Come funziona",
    "home.how.title": "Dall’avvio al verdetto in quattro chiamate",

    "home.compliance.privacy": "Privacy by design",
    "home.compliance.minimization": "Minimizzazione dei dati",
    "home.compliance.encryption": "TLS in transito · AES a riposo",
    "home.compliance.dpa": "Verdetti immutabili · DPA",

    "home.cta.title": "Aggiungi il liveness al tuo flusso in un pomeriggio",
    "home.cta.subtitle": "Avvia un tenant sandbox in minuti o parla con noi di scala e on-premise.",

    "footer.tagline": "Una persona reale e viva — verificata in un redirect.",
    "footer.product": "Prodotto",
    "footer.usecases": "Casi d’uso",
    "footer.company": "Azienda",
    "footer.legal": "Legale",
    "footer.privacy": "Informativa privacy",
    "footer.terms": "Termini di servizio",
    "footer.rights": "Tutti i diritti riservati.",
    "footer.madeBy": "Un’azienda sec4us",

    "cookie.text": "Usiamo cookie per far funzionare il sito e migliorare la tua esperienza. Gli utenti verificati da SecFace vedono sempre un chiaro avviso di consenso e registrazione nella pagina ospitata.",
    "cookie.accept": "Accetta",
    "cookie.decline": "Rifiuta",

    "nf.title": "Pagina non trovata",
    "nf.body": "La pagina che cerchi non esiste o è stata spostata.",
  },
};
